import { createTypes, completeTypes } from 'redux-recompose';
import Polling from '@widergy/polling-handling';
import { retrieveErrors } from '@widergy/web-utils/lib/message';
import { isEmpty } from '@widergy/web-utils/lib/array';
import { push } from 'connected-react-router';

import { appConfig } from 'config/customization';
import UtilityService from 'services/UtilityService';
import { message } from 'utils/stringUtils';
import { ROUTES } from 'constants/routes';

import { hasAvailableSlots, getAvailableSlots } from './utils';

export const actions = createTypes(
  completeTypes(
    [
      'HEALTH_CHECK',
      'GET_COMPLAINT_FORM',
      'GET_AUTOMATIC_DEBIT_FORM',
      'GET_DIGITAL_BILL_FORM',
      'GET_CONFIG',
      'GET_OFFICES',
      'GET_OFFICE_AVAILABILITY_DAYS',
      'GET_OFFICE_SLOT'
    ],
    ['CLEAN_OFFICE_DATA', 'CLEAN_OFFICE_SLOT']
  ),
  '@@UTILITY'
);

const privateActionCreators = {
  healthCheckSuccess: payload => ({
    type: actions.HEALTH_CHECK_SUCCESS,
    target: 'healthCheck',
    payload
  }),
  healthCheckFailure: error => ({
    type: actions.HEALTH_CHECK_FAILURE,
    target: 'healthCheck',
    payload: error
  }),
  getComplaintFormSuccess: payload => ({
    type: actions.GET_COMPLAINT_FORM_SUCCESS,
    target: 'complaintForm',
    payload
  }),
  getComplaintFormFailure: error => ({
    type: actions.GET_COMPLAINT_FORM_FAILURE,
    target: 'complaintForm',
    payload: error
  }),
  getAutomaticDebitFormSuccess: payload => ({
    type: actions.GET_AUTOMATIC_DEBIT_FORM_SUCCESS,
    target: 'automaticDebitForm',
    payload
  }),
  getAutomaticDebitFormFailure: error => ({
    type: actions.GET_AUTOMATIC_DEBIT_FORM_FAILURE,
    target: 'automaticDebitForm',
    payload: error
  }),
  getDigitalBillFormSuccess: payload => ({
    type: actions.GET_DIGITAL_BILL_FORM_SUCCESS,
    target: 'digitalBillForm',
    payload
  }),
  getDigitalBillFormFailure: error => ({
    type: actions.GET_DIGITAL_BILL_FORM_FAILURE,
    target: 'digitalBillForm',
    payload: error
  }),
  getConfigSuccess: payload => ({
    type: actions.GET_CONFIG_SUCCESS,
    target: 'appConfiguration',
    payload
  }),
  getConfigFailure: () => ({
    type: actions.GET_CONFIG_FAILURE,
    target: 'appConfiguration'
  }),
  getOfficesTurnsSuccess: (payload, target) => ({
    type: actions.GET_OFFICES_SUCCESS,
    target,
    payload
  }),
  getOfficesTurnsFailure: (error, target) => ({
    type: actions.GET_OFFICES_FAILURE,
    target,
    payload: error
  }),
  getOfficeAvailabilityDaysSuccess: payload => ({
    type: actions.GET_OFFICE_AVAILABILITY_DAYS_SUCCESS,
    target: 'officeAvailabilityDays',
    payload
  }),
  getOfficeAvailabilityDaysFailure: error => ({
    type: actions.GET_OFFICE_AVAILABILITY_DAYS_FAILURE,
    target: 'officeAvailabilityDays',
    payload: error
  }),
  getOfficeSlotSuccess: payload => ({
    type: actions.GET_OFFICE_SLOT_SUCCESS,
    target: 'officeSlot',
    payload
  }),
  getOfficeSlotFailure: error => ({
    type: actions.GET_OFFICE_SLOT_FAILURE,
    target: 'officeSlot',
    payload: error
  })
};

export const actionCreators = {
  healthCheck: () => async dispatch => {
    dispatch({ type: actions.HEALTH_CHECK, target: 'healthCheck' });
    const response = await UtilityService.healthCheck();
    if (response.ok) {
      dispatch(privateActionCreators.healthCheckSuccess(response.data));
      if (response.data.healthy) await dispatch(actionCreators.getConfig());
      else dispatch(push(ROUTES.MAINTENANCE));
    } else {
      dispatch(privateActionCreators.healthCheckFailure(response.problem));
      dispatch(push(ROUTES.MAINTENANCE));
    }
  },

  getComplaintForm: () => async dispatch => {
    dispatch({ type: actions.GET_COMPLAINT_FORM, target: 'complaintForm' });
    const response = await UtilityService.getComplaintForm();
    if (response.ok) {
      dispatch(privateActionCreators.getComplaintFormSuccess(response.data));
    } else dispatch(privateActionCreators.getComplaintFormFailure(response.data));
  },

  getAutomaticDebitForm: () => async dispatch => {
    dispatch({ type: actions.GET_AUTOMATIC_DEBIT_FORM, target: 'automaticDebitForm' });
    const response = await UtilityService.getAutomaticDebitForm();
    if (response.ok) {
      dispatch(privateActionCreators.getAutomaticDebitFormSuccess(response.data));
    } else dispatch(privateActionCreators.getAutomaticDebitFormFailure(response.data));
  },

  getDigitalBillForm: () => async dispatch => {
    if (!appConfig.digitalBillWithForm) return;
    dispatch({ type: actions.GET_DIGITAL_BILL_FORM, target: 'digitalBillForm' });
    const response = await UtilityService.getDigitalBillForm();
    if (response.ok) {
      dispatch(privateActionCreators.getDigitalBillFormSuccess(response.data));
    } else dispatch(privateActionCreators.getDigitalBillFormFailure(response.data));
  },
  getConfig: () => async dispatch => {
    dispatch({ type: actions.GET_CONFIG, target: 'appConfiguration' });
    const response = await Polling.handle(UtilityService.getConfig());
    if (response.error) {
      dispatch(privateActionCreators.getConfigFailure(response.error));
    } else dispatch(privateActionCreators.getConfigSuccess(response.data));
  },

  getOffices: (category, canGiveTurns) => async dispatch => {
    dispatch({ type: actions.GET_OFFICES, target: `${category}Offices` });
    const response = await UtilityService.getOffices(category, canGiveTurns);
    if (response.ok) {
      if (isEmpty(response.data)) {
        dispatch(
          privateActionCreators.getOfficesTurnsFailure(
            message('DefaultMessages:getOfficesFailure'),
            `${category}Offices`
          )
        );
      } else dispatch(privateActionCreators.getOfficesTurnsSuccess(response.data[0], `${category}Offices`));
    } else {
      const error = retrieveErrors(response.data, message('DefaultMessages:getOfficesFailure'));
      dispatch(privateActionCreators.getOfficesTurnsFailure(error, `${category}Offices`));
    }
  },

  getOfficeAvailabilityDays: (office, month, year) => async dispatch => {
    dispatch({ type: actions.GET_OFFICE_AVAILABILITY_DAYS, target: 'officeAvailabilityDays' });
    const response = await UtilityService.getOfficeAvailabilityDays(office, month, year);
    if (response.ok) {
      dispatch(privateActionCreators.getOfficeAvailabilityDaysSuccess(response.data));
    } else {
      const error = retrieveErrors(response.data, message('DefaultMessages:getOfficeDaysFailure'));
      dispatch(privateActionCreators.getOfficeAvailabilityDaysFailure(error));
    }
  },

  cleanOfficeSlot: () => dispatch => dispatch({ type: actions.CLEAN_OFFICE_SLOT }),

  getOfficeSlot: (office, day) => async dispatch => {
    dispatch(actionCreators.cleanOfficeSlot());
    dispatch({ type: actions.GET_OFFICE_SLOT, target: 'officeSlot' });
    const response = await UtilityService.getOfficeSlot(office, day);
    if (response.ok && hasAvailableSlots(response.data)) {
      dispatch(
        privateActionCreators.getOfficeSlotSuccess(getAvailableSlots(response.data.working_day_slots))
      );
    } else {
      const error = retrieveErrors(response.data, message('DefaultMessages:getOfficeSlotFailure'));
      dispatch(privateActionCreators.getOfficeSlotFailure(error));
    }
  },

  cleanOfficeData: () => dispatch => dispatch({ type: actions.CLEAN_OFFICE_DATA })
};
